import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { TextField, MenuItem, Typography, Switch } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";

import { companiesSelector } from "selectors/companiesSelectors";
import { getCompanyConfig } from "selectors/appSelector";
import { getCompaniesAction } from "actions/companiesActions";
import { loadCompanyConfigAction } from "actions/appActions";

import { ModelDialog } from "components/Model";
import Tooltip from "components/Tooltip/Tooltip";

export const ProjectConfiguration = () => {
  const dispatch = useDispatch();
  const { companiesList } = useSelector(companiesSelector) || [];

  const [selectedCompany, setCompany] = useState({});

  const fetchCompaniesList = useCallback(() => {
    dispatch(getCompaniesAction({ pageSize: 500, pageNumber: 1 }));
  }, [dispatch]);

  useEffect(() => {
    fetchCompaniesList();
  }, [fetchCompaniesList]);

  return (
    <div className="admin-proj-conf-container m-3 mt-4">
      <div>
        <div>
          <strong>
            Select a company to load and configure its Project settings
            ("Project As Property" option). Choose from the list below to
            continue.
          </strong>
        </div>
        <div className="admin-com-select-con d-flex mt-3 align-center">
          <div col="col-5 me-2">
            <lable>
              <strong>Company: </strong>
            </lable>
          </div>
          <div className="col-7 ms-4">
            <TextField
              type={"select"}
              select={true}
              className="form-field"
              variant="outlined"
              onChange={(e) => {}}
            >
              {companiesList.map((company) => (
                <MenuItem
                  key={company.companyId}
                  value={company.companyId}
                  company={company}
                  onClick={() => {
                    setCompany(company);
                  }}
                >
                  <Typography>{company.companyName}</Typography>
                </MenuItem>
              ))}
            </TextField>
          </div>
        </div>
      </div>
      <div className="mt-5 divider"></div>
      <div className="admin-settings-config">
        <ProjectAsProperty company={selectedCompany} />
      </div>
    </div>
  );
};

const ProjectAsProperty = ({ company = {} }) => {
  const { companyId = null } = company;
  const dispatch = useDispatch();

  const [settingsState, setSettingsState] = useState({
    projEqualProp: false,
  });
  const [disableSaveButton, setDisableSaveButton] = useState(true);
  const [showDialog, setShowDialog] = useState(false);

  const companyConfig = useSelector(getCompanyConfig);

  const getCompanyDetails = useCallback(() => {
    if (companyId) {
      dispatch(loadCompanyConfigAction({ companyId }, "GET"));
    }
  }, [dispatch, companyId]);

  useEffect(() => {
    getCompanyDetails();

  }, [getCompanyDetails]);

  useEffect(() => {
    setSettingsState(companyConfig);
  }, [companyConfig]);

  useEffect(() => {
    const isDisabled = Object.keys(settingsState).every(
      (key) => settingsState[key] === companyConfig[key]
    );
    setDisableSaveButton(isDisabled);
  }, [companyConfig, settingsState]);

  const handleSave = () => {
    // Here you would typically dispatch an action to save the settings
    // For now, we will just log the settings to the console
    dispatch(
      loadCompanyConfigAction(
        { ...settingsState, companyId: company.companyId },
        "POST"
      )
    ); // Disable the button after saving
  };

  const ShowConfirmationMessage = () => (
    <div>
      <div>
        <Typography className="body1" gutterBottom>
          Are you sure you want to update the "Project as Property" setting?
        </Typography>
        <Typography className="body1" gutterBottom>
          Changing this setting will modify how projects and properties are
          created and managed within the system.
          <strong>
            {" "}
            Once saved, the system's behavior and interface related to Projects
            and Properties will be updated accordingly.
          </strong>
        </Typography>
        <Typography className="body1" gutterBottom>
          <strong>Important:</strong> Once saved, notify Company users to
          signout and SignIn again to reflect the updated settings in their
          application.
        </Typography>
        <Typography variant="body1" color="error">
          Please proceed only if you are certain, as this change may affect how
          data is structured and used across the platform.
        </Typography>
      </div>
      <div className="mt-2 d-flex justify-content-center mt-4">
        <button
          style={{ width: "6rem" }}
          className="secondaryButton"
          onClick={() => {
            setShowDialog(false);
            setSettingsState(companyConfig);
          }}
        >
          Cancel
        </button>
        <button
          style={{ width: "6rem" }}
          className="primaryButton ms-2"
          onClick={() => {
            handleSave();
            setShowDialog(false);
          }}
        >
          Confirm
        </button>
      </div>
    </div>
  );

  if (!companyId) {
    return (
      <div>Please select company from the list to configure the settings</div>
    );
  }

  return (
    <div className="general-settings-container p-2">
      <ModelDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        dialogContent={<ShowConfirmationMessage />}
        onConfirm={handleSave}
        confirmText="Save"
      />
      <div>
        <Typography className="h5 mt-4">
          You are configuring the settings for Company:{" "}
          <strong>{company?.companyName}</strong>
        </Typography>
      </div>
      <div className="d-flex align-items-center">
        <Typography className="h5 mt-4">
          Project As Property{" "}
          <Tooltip title="When enabled, the project will be treated as a property. This means the project will have same functionalities as a property, every project will be considered as a property.">
            <span className="pointer">
              <InfoOutlined fontSize="10" />
            </span>
          </Tooltip>
          <Switch
            value="check"
            className="ms-2"
            checked={settingsState.projEqualProp}
            onChange={() =>
              setSettingsState((prevState) => {
                return {
                  ...prevState,
                  projEqualProp: !prevState.projEqualProp,
                };
              })
            }
          />
        </Typography>
      </div>
      {/* <div>
        <Typography className="h6 mt-2">
          <i>
            You previously configured Project as Property. This setting cannot
            be changed through the app. Please contact our Support team for
            assistance.
          </i>
        </Typography>
      </div> */}
      <div className="d-flex justify-content-center mt-4">
        <button
          className={`primaryButton col-6 ${
            disableSaveButton ? "disabledButton" : ""
          }`}
          disabled={disableSaveButton}
          onClick={() => setShowDialog(true)}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default ProjectConfiguration;
